import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { offersAPI, freeOffersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { SortableTableHead, useTableSort } from "../components/ui/sortable-table-head";
import { toast } from "sonner";
import { Plus, Search, FileText, Eye, Euro, Archive, ArchiveRestore, User, PenLine, Flame } from "lucide-react";

export default function OffersPage() {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  
  // Sorting state - default to newest first
  const { sortColumn, sortDirection, handleSort, sortData } = useTableSort("created_at", "desc");
  
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    loadOffers();
  }, [showArchived]);

  const loadOffers = async () => {
    try {
      // Load both regular and free-text offers
      const [regularResponse, freeResponse] = await Promise.all([
        offersAPI.list(null, showArchived),
        freeOffersAPI.list().catch(() => ({ data: [] }))
      ]);
      
      // Mark free offers with a flag and normalize structure
      const freeOffers = (freeResponse.data || []).map(offer => ({
        ...offer,
        is_free_offer: true,
        project_number: null,
        project_id: null,
        // Map total fields for consistency
        gross_total: offer.total,
        net_total: offer.subtotal
      }));
      
      // Combine and sort by created_at (newest first)
      const allOffers = [...regularResponse.data, ...freeOffers].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      
      setOffers(allOffers);
    } catch (error) {
      console.error("Failed to load offers:", error);
      toast.error("Fehler beim Laden der Angebote");
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (offerId) => {
    try {
      await offersAPI.archive(offerId);
      toast.success("Angebot archiviert");
      loadOffers();
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    }
  };

  const handleUnarchive = async (offerId) => {
    try {
      await offersAPI.unarchive(offerId);
      toast.success("Angebot wiederhergestellt");
      loadOffers();
    } catch (error) {
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR"
    }).format(amount || 0);
  };

  const filteredOffers = offers.filter(offer => {
    const searchLower = searchTerm.toLowerCase();
    return (
      offer.offer_number?.toLowerCase().includes(searchLower) ||
      offer.customer_name?.toLowerCase().includes(searchLower) ||
      offer.project_number?.toLowerCase().includes(searchLower)
    );
  });

  // Apply sorting
  const sortedOffers = sortData(filteredOffers, {
    display_price: (a, b) => {
      const aPrice = a.is_free_offer ? (a.total || 0) : (a.display_price || 0);
      const bPrice = b.is_free_offer ? (b.total || 0) : (b.display_price || 0);
      return aPrice - bPrice;
    }
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in" data-testid="offers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Angebote</h1>
          <p className="text-muted-foreground">
            Alle erstellten Angebote
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button asChild variant="outline" data-testid="new-free-offer-btn">
              <Link to="/freitext-angebote/neu">
                <PenLine className="h-4 w-4 mr-2" />
                Freitext Angebot
              </Link>
            </Button>
          )}
          {isAdmin && (
            <Button asChild variant="outline" data-testid="new-wp-offer-btn-top">
              <Link to="/wp-angebote">
                <Flame className="h-4 w-4 mr-2 text-orange-500" />
                Wärmepumpen-Angebote
              </Link>
            </Button>
          )}
          <Button asChild data-testid="new-offer-btn">
            <Link to="/angebote/neu">
              <Plus className="h-4 w-4 mr-2" />
              Neues Angebot
            </Link>
          </Button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Suche nach Angebotsnummer, Kunde, Projekt..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-12"
            data-testid="offer-search"
          />
        </div>
        <div className="flex items-center space-x-2">
          <Switch
            id="show-archived"
            checked={showArchived}
            onCheckedChange={setShowArchived}
          />
          <Label htmlFor="show-archived" className="text-sm text-muted-foreground">
            Archivierte anzeigen
          </Label>
        </div>
      </div>

      {/* Offers List */}
      {sortedOffers.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    column="offer_number"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Angebotsnr.
                  </SortableTableHead>
                  <SortableTableHead
                    column="customer_name"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    Kunde
                  </SortableTableHead>
                  <TableHead className="hidden md:table-cell">Anlage</TableHead>
                  <TableHead className="hidden lg:table-cell">Erstellt von</TableHead>
                  <SortableTableHead
                    column="status"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="hidden md:table-cell"
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    column="display_price"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="text-right"
                  >
                    Preis
                  </SortableTableHead>
                  <SortableTableHead
                    column="created_at"
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    onSort={handleSort}
                    className="hidden lg:table-cell"
                  >
                    Datum
                  </SortableTableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOffers.map((offer) => (
                  <TableRow key={offer.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell className="font-mono text-sm">
                      <div className="flex items-center gap-2">
                        {offer.offer_number}
                        {offer.is_free_offer && (
                          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                            Freitext
                          </Badge>
                        )}
                        {offer.is_legacy_import && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                            Import
                          </Badge>
                        )}
                        <IncognitoBadge active={offer.is_incognito} />
                      </div>
                      {offer.is_legacy_import && offer.legacy_offer_number && offer.legacy_offer_number !== offer.offer_number && (
                        <p className="text-xs text-muted-foreground">ehem. {offer.legacy_offer_number}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{offer.customer_name}</p>
                        {offer.is_free_offer ? (
                          <p className="text-sm text-amber-600 italic">{offer.title}</p>
                        ) : (
                          <p className="text-sm text-muted-foreground font-mono">
                            {offer.project_number}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {offer.is_free_offer ? (
                        <span className="text-muted-foreground text-sm">&mdash;</span>
                      ) : offer.is_legacy_import ? (
                        <div className="text-sm">
                          <p className="font-medium">{offer.title || "Importiertes Angebot"}</p>
                          <p className="text-muted-foreground text-xs">
                            {offer.offer_date ? new Date(offer.offer_date).toLocaleDateString("de-DE") : ""}
                          </p>
                        </div>
                      ) : (
                        <div className="text-sm">
                          <p>{typeof offer.kwp === 'number' ? offer.kwp.toFixed(2) : offer.kwp} kWp</p>
                          <p className="text-muted-foreground">{offer.storage_size} kWh Speicher</p>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="text-sm flex items-center gap-1.5">
                        {offer.created_by_name ? (
                          <>
                            <User className="h-3.5 w-3.5 text-muted-foreground" />
                            <span>{offer.created_by_name}</span>
                          </>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        {offer.is_free_offer ? (
                          <Badge className="bg-amber-100 text-amber-800">
                            {offer.status === "offen" || !offer.status ? "Offen" : offer.status}
                          </Badge>
                        ) : offer.is_legacy_import ? (
                          <Badge className={`${
                            offer.status === "accepted" || offer.status === "angenommen" ? "bg-green-100 text-green-800" : 
                            offer.status === "rejected" || offer.status === "abgelehnt" ? "bg-red-100 text-red-800" : 
                            offer.status === "gesendet" ? "bg-blue-100 text-blue-800" :
                            "bg-yellow-100 text-yellow-800"
                          }`}>
                            {offer.status === "accepted" || offer.status === "angenommen" ? "Angenommen" : 
                             offer.status === "rejected" || offer.status === "abgelehnt" ? "Abgelehnt" : 
                             offer.status === "gesendet" ? "Gesendet" :
                             offer.status === "erstellt" ? "Erstellt" :
                             "Erstellt"}
                          </Badge>
                        ) : (
                          <Badge className={`status-${
                            offer.status === "angenommen" ? "angebot_angenommen" : 
                            offer.status === "abgelehnt" ? "angebot_abgelehnt" : 
                            offer.offer_sent_at ? "angebot_gesendet" : "angebot_erstellt"
                          }`}>
                            {offer.status === "angenommen" ? "Angenommen" : 
                             offer.status === "abgelehnt" ? "Abgelehnt" : 
                             offer.offer_sent_at ? "Gesendet" : "Offen"}
                          </Badge>
                        )}
                        {offer.archived && (
                          <Badge variant="outline" className="text-muted-foreground">
                            Archiviert
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="font-semibold font-['Manrope']">
                        {formatCurrency(
                          offer.is_free_offer 
                            ? offer.total 
                            : offer.is_legacy_import 
                              ? offer.total_gross 
                              : offer.display_price
                        )}
                      </div>
                      {!offer.is_free_offer && !offer.is_legacy_import && offer.discount_percent > 0 && (
                        <p className="text-xs text-green-600">-{offer.discount_percent}% Rabatt</p>
                      )}
                      {offer.is_legacy_import && offer.discount_amount > 0 && (
                        <p className="text-xs text-green-600">-{formatCurrency(offer.discount_amount)} Rabatt</p>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                      {offer.created_at && new Date(offer.created_at).toLocaleDateString("de-DE")}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {offer.is_free_offer ? (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              asChild
                              title="Angebot ansehen"
                              data-testid={`view-free-offer-${offer.id}`}
                            >
                              <Link to={`/freitext-angebote/${offer.id}`}>
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => window.open(freeOffersAPI.getPdfUrl(offer.id), "_blank")}
                              title="PDF anzeigen"
                              data-testid={`pdf-free-offer-${offer.id}`}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                          </>
                        ) : offer.is_legacy_import ? (
                          /* Legacy-Import: Zur spezifischen Import-Detailseite navigieren */
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            title="Legacy-Angebot ansehen"
                            data-testid={`view-legacy-offer-${offer.id}`}
                          >
                            <Link to={`/angebote-import/${offer.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="icon"
                            asChild
                            data-testid={`view-offer-${offer.id}`}
                          >
                            <Link to={`/angebote/${offer.id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                        )}
                        {!offer.is_free_offer && offer.status === "abgelehnt" && !offer.archived && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleArchive(offer.id)}
                            title="Archivieren"
                          >
                            <Archive className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        {!offer.is_free_offer && offer.archived && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleUnarchive(offer.id)}
                            title="Wiederherstellen"
                          >
                            <ArchiveRestore className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <Card className="py-12">
          <CardContent className="text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">Keine Angebote gefunden</h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? "Versuchen Sie eine andere Suche" : "Erstellen Sie Ihr erstes Angebot"}
            </p>
            {!searchTerm && (
              <Button asChild>
                <Link to="/angebote/neu">
                  <Plus className="h-4 w-4 mr-2" />
                  Neues Angebot
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
